import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowDown, Check, Upload, Star, ChefHat, MessagesSquare } from "lucide-react";
import { toast } from "sonner";
import { api, imgSrc } from "@/lib/api";
import { Reveal } from "@/components/Reveal";

const APPEAR_OPTIONS = [
  "Celebrity / Featured Guest",
  "Chef / Culinary Guest",
  "Entrepreneur / Business Owner",
  "Community Leader",
  "Artist / Entertainer",
  "Athlete",
  "Share My Story",
  "Other",
];

const WAYS = [
  {
    icon: Star,
    title: "Be a Guest",
    copy: "For entertainers, entrepreneurs, athletes, public figures, community leaders and personalities with something worth talking about.",
    cta: "Tell Us Your Story",
    appear: "Celebrity / Featured Guest",
    image: "/assets/hero-original.jpg",
  },
  {
    icon: ChefHat,
    title: "Cook at the Pool",
    copy: "For chefs, culinary personalities, restaurant owners and food creators interested in cooking, competing or showcasing a signature dish.",
    cta: "Step Into the Kitchen",
    appear: "Chef / Culinary Guest",
    image: "/assets/hero-kitchen.jpg",
  },
  {
    icon: MessagesSquare,
    title: "Share Your Story",
    copy: "For inspiring people, businesses, organizations, causes and community stories that deserve a larger platform.",
    cta: "Submit Your Story",
    appear: "Share My Story",
    image: "/assets/cafe-event.jpg",
  },
];

const inputCls =
  "w-full border border-slate-300 bg-white px-4 py-3.5 rounded-lg text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-pool transition-colors";

export default function BeOnTheShow() {
  const [appearAs, setAppearAs] = useState(APPEAR_OPTIONS[0]);
  const [form, setForm] = useState({ name: "", email: "", phone: "", city_state: "", instagram: "", website: "", about: "", topic: "", media_link: "" });
  const [photo, setPhoto] = useState(null);
  const [photoUrl, setPhotoUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const applyRef = useRef(null);
  const chefsRef = useRef(null);
  const fileRef = useRef(null);

  const scrollTo = (ref) => ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  const pick = (appear, ref) => {
    setAppearAs(appear);
    scrollTo(ref || applyRef);
  };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const uploadPhoto = async (file) => {
    if (!file) return;
    setPhoto(file);
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/casting/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPhotoUrl(r.data.url || r.data.path || "");
    } catch {
      toast.error("Photo upload failed — try an image under 10MB");
      setPhoto(null);
    }
    setUploading(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    try {
      await api.post("/casting", { ...form, appear_as: appearAs, photo_url: photoUrl });
      setSent(true);
      toast.success("Submitted — the Poolside Cooking team will review your application.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Something went wrong. Please try again.");
    }
    setBusy(false);
  };

  return (
    <div data-testid="be-on-show-page">
      {/* HERO */}
      <section className="bg-cream relative overflow-hidden" data-testid="be-on-show-hero">
        <div className="container-site grid lg:grid-cols-2 gap-12 lg:gap-16 items-center py-16 sm:py-20">
          <Reveal>
            <p className="label-eyebrow mb-5">Be on Poolside Cooking</p>
            <h1 className="font-display font-black tracking-tight leading-[1.02] text-4xl sm:text-5xl lg:text-[3.4rem] text-ink" data-testid="be-on-show-headline">
              You Bring the Story.<br />
              <span className="text-pool">We&rsquo;ll Pull Up the Chair.</span>
            </h1>
            <p className="mt-6 text-slate-600 leading-relaxed max-w-lg text-sm sm:text-base" data-testid="be-on-show-copy">
              Poolside Cooking is always looking for great personalities, unforgettable stories and people who know
              how to bring good food, good vibes and good conversation.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <button
                onClick={() => scrollTo(applyRef)}
                data-testid="hero-apply-cta"
                className="inline-flex items-center justify-center gap-3 bg-pool hover:bg-pool-dark text-white font-bold text-xs sm:text-sm tracking-[0.15em] uppercase px-8 py-4 rounded-full transition-all hover:-translate-y-0.5"
              >
                Apply to Be on the Show <ArrowRight className="h-4 w-4" />
              </button>
              <button
                onClick={() => scrollTo(chefsRef)}
                data-testid="hero-chefs-cta"
                className="inline-flex items-center justify-center gap-3 border border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white font-bold text-xs sm:text-sm tracking-[0.15em] uppercase px-8 py-4 rounded-full transition-colors"
              >
                Chefs — This One&rsquo;s for You <ArrowDown className="h-4 w-4" />
              </button>
            </div>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="relative h-[420px] sm:h-[500px]" data-testid="be-on-show-collage">
              <div className="absolute left-0 top-0 w-[62%] -rotate-2">
                <div className="bg-white p-2 rounded-2xl shadow-[0_24px_60px_rgba(2,20,30,0.18)]">
                  <div className="aspect-[4/3] overflow-hidden rounded-xl">
                    <img src="/assets/hero-original.jpg" alt="Ashley Marie interviewing Doug E. Fresh on Poolside Cooking" className="h-full w-full object-cover object-[center_20%]" loading="lazy" />
                  </div>
                </div>
              </div>
              <div className="absolute right-0 top-[18%] w-[46%] rotate-2 z-10">
                <div className="bg-white p-2 rounded-2xl shadow-[0_24px_60px_rgba(2,20,30,0.22)]">
                  <div className="aspect-[3/4] overflow-hidden rounded-xl">
                    <img src="/assets/ashley-host-lavender.jpg" alt="Ashley Marie, host of Poolside Cooking" className="h-full w-full object-cover object-top" loading="lazy" />
                  </div>
                </div>
              </div>
              <div className="absolute left-[10%] bottom-0 w-[44%] rotate-1 z-20">
                <div className="bg-white p-2 rounded-2xl shadow-[0_24px_60px_rgba(2,20,30,0.2)]">
                  <div className="aspect-[4/3] overflow-hidden rounded-xl">
                    <img src="/assets/cafe-food.jpg" alt="A cooking moment from Poolside Cooking" className="h-full w-full object-cover" loading="lazy" />
                  </div>
                </div>
              </div>
              <div className="absolute -right-3 bottom-[8%] h-24 w-24 rounded-full border-[6px] border-pool/30 pointer-events-none" aria-hidden="true" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* WAYS TO BE ON POOLSIDE */}
      <section className="bg-white py-16 sm:py-20" data-testid="ways-section">
        <div className="container-site">
          <Reveal className="max-w-2xl mb-12">
            <p className="label-eyebrow mb-4">Ways to Be on Poolside</p>
            <h2 className="font-display font-black tracking-tight leading-[1.0] text-3xl sm:text-4xl lg:text-5xl text-ink">
              Three Chairs. <span className="text-pool">Pick Yours.</span>
            </h2>
          </Reveal>
          <div className="grid md:grid-cols-3 gap-6" data-testid="ways-grid">
            {WAYS.map((w, i) => (
              <Reveal key={w.title} delay={i * 0.1} className="h-full">
                <div className="group h-full bg-cream rounded-2xl overflow-hidden border border-slate-100 hover:shadow-[0_24px_60px_rgba(2,20,30,0.12)] hover:-translate-y-1 transition-all duration-500 flex flex-col" data-testid={`way-${w.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}>
                  <div className="aspect-[16/10] overflow-hidden">
                    <img src={w.image} alt={w.title} loading="lazy" className="img-zoom h-full w-full object-cover" />
                  </div>
                  <div className="p-7 flex flex-col flex-1">
                    <w.icon className="h-5 w-5 text-pool mb-4" />
                    <h3 className="font-display font-black text-xl text-ink">{w.title}</h3>
                    <p className="mt-3 text-sm text-slate-600 leading-relaxed flex-1">{w.copy}</p>
                    <button
                      onClick={() => pick(w.appear)}
                      data-testid={`way-cta-${w.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                      className="mt-6 inline-flex items-center gap-2.5 text-pool hover:text-pool-dark font-bold text-[11px] tracking-[0.18em] uppercase transition-colors"
                    >
                      {w.cta} <ArrowRight className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURED CASTING — CHEF, WHERE YOU AT? */}
      <section ref={chefsRef} className="water-panel text-white noise-overlay py-16 sm:py-20 scroll-mt-20" data-testid="chef-casting-section">
        <img src="/assets/water.jpg" alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover opacity-20 pointer-events-none" />
        <div className="relative container-site grid lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <p className="label-eyebrow mb-5">Calling All Chefs</p>
            <h2 className="font-display font-black tracking-tight leading-[0.98] text-5xl sm:text-6xl lg:text-7xl" data-testid="chef-casting-headline">
              Chef,<br />
              <span className="font-script font-semibold text-pool-light tracking-normal">Where You At?</span>
            </h2>
            <p className="mt-6 text-white/85 leading-relaxed max-w-md text-sm sm:text-base">
              Think you can bring the flavor Poolside? We&rsquo;re looking for chefs with personality, creativity and
              a signature dish worth talking about.
            </p>
            <p className="mt-4 text-pool-light font-display font-bold text-xs sm:text-sm tracking-[0.2em] uppercase">
              Cook. Compete. Showcase your food. Tell your story.
            </p>
            <button
              onClick={() => pick("Chef / Culinary Guest")}
              data-testid="im-the-chef-cta"
              className="mt-8 inline-flex items-center gap-3 bg-white text-ink hover:bg-pool-light font-bold text-xs sm:text-sm tracking-[0.15em] uppercase px-8 py-4 rounded-full transition-all hover:-translate-y-0.5"
            >
              I&rsquo;m the Chef <ArrowRight className="h-4 w-4" />
            </button>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="relative" data-testid="chef-casting-image">
              <div className="absolute -inset-3 rounded-3xl border-2 border-white/25 translate-x-4 translate-y-4" aria-hidden="true" />
              <div className="relative aspect-[4/3] overflow-hidden rounded-2xl shadow-2xl">
                <img src="/assets/hero-kitchen.jpg" alt="A chef cooking in the Poolside kitchen" loading="lazy" className="img-zoom h-full w-full object-cover" />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* APPLICATION */}
      <section ref={applyRef} className="bg-white py-16 sm:py-20 scroll-mt-20" data-testid="apply-section">
        <div className="container-site max-w-3xl">
          <Reveal className="text-center mb-12">
            <p className="label-eyebrow mb-4 justify-center">Applications</p>
            <h2 className="font-display font-black tracking-tight leading-[1.0] text-3xl sm:text-4xl lg:text-5xl text-ink" data-testid="apply-headline">
              Ready to Pull Up <span className="text-pool">a Chair?</span>
            </h2>
          </Reveal>

          {sent ? (
            <Reveal>
              <div className="bg-pool-light/40 border border-pool/25 rounded-2xl p-10 text-center" data-testid="apply-confirmation">
                <div className="mx-auto h-14 w-14 rounded-full bg-pool flex items-center justify-center">
                  <Check className="h-7 w-7 text-white" />
                </div>
                <h3 className="mt-5 font-display font-black text-2xl text-ink">You&rsquo;re in the mix.</h3>
                <p className="mt-3 text-sm text-slate-600 max-w-md mx-auto leading-relaxed">
                  Thanks for applying to Poolside Cooking. Our production team reviews every application and will
                  reach out if there&rsquo;s a fit for an upcoming episode.
                </p>
              </div>
            </Reveal>
          ) : (
            <Reveal>
              <form onSubmit={submit} className="bg-cream rounded-2xl border border-slate-100 p-6 sm:p-10" data-testid="casting-form">
                <div className="grid sm:grid-cols-2 gap-4">
                  <input required value={form.name} onChange={set("name")} placeholder="Full Name *" className={inputCls} data-testid="casting-name" />
                  <input required type="email" value={form.email} onChange={set("email")} placeholder="Email *" className={inputCls} data-testid="casting-email" />
                  <input value={form.phone} onChange={set("phone")} placeholder="Phone" className={inputCls} data-testid="casting-phone" />
                  <input value={form.city_state} onChange={set("city_state")} placeholder="City / State" className={inputCls} data-testid="casting-city" />
                  <input value={form.instagram} onChange={set("instagram")} placeholder="Instagram / Social Media" className={inputCls} data-testid="casting-instagram" />
                  <input value={form.website} onChange={set("website")} placeholder="Website" className={inputCls} data-testid="casting-website" />
                </div>

                <div className="mt-6">
                  <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-500 mb-3">How would you like to appear?</p>
                  <div className="flex flex-wrap gap-2.5" data-testid="casting-appear-as">
                    {APPEAR_OPTIONS.map((o) => (
                      <button
                        type="button"
                        key={o}
                        onClick={() => setAppearAs(o)}
                        data-testid={`appear-${o.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                        className={`text-xs font-semibold px-4 py-2.5 rounded-full border transition-colors ${
                          appearAs === o ? "bg-pool text-white border-pool" : "bg-white text-slate-600 border-slate-200 hover:border-pool"
                        }`}
                      >
                        {o}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="mt-6 space-y-4">
                  <textarea required value={form.about} onChange={set("about")} placeholder="Tell us about yourself *" rows={4} className={`${inputCls} resize-none`} data-testid="casting-about" />
                  <textarea value={form.topic} onChange={set("topic")} placeholder="What would you like to talk about or showcase?" rows={3} className={`${inputCls} resize-none`} data-testid="casting-topic" />
                </div>

                <div className="mt-6 grid sm:grid-cols-2 gap-4 items-start">
                  <div>
                    <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-500 mb-3">Photo / Headshot</p>
                    <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => uploadPhoto(e.target.files?.[0])} data-testid="casting-photo-input" />
                    <button
                      type="button"
                      onClick={() => fileRef.current?.click()}
                      className="w-full inline-flex items-center justify-center gap-2.5 border-2 border-dashed border-slate-300 hover:border-pool rounded-xl px-4 py-6 text-sm text-slate-500 hover:text-pool transition-colors bg-white"
                      data-testid="casting-photo-button"
                    >
                      <Upload className="h-4 w-4" />
                      {uploading ? "Uploading…" : photo ? photo.name : "Upload a photo (optional)"}
                    </button>
                  </div>
                  <div>
                    <p className="text-[10px] tracking-[0.2em] uppercase font-bold text-slate-500 mb-3">Video / Media Link</p>
                    <input value={form.media_link} onChange={set("media_link")} placeholder="Optional video or media link" className={inputCls} data-testid="casting-media-link" />
                  </div>
                </div>

                <button
                  type="submit"
                  disabled={busy || uploading}
                  className="mt-8 w-full inline-flex items-center justify-center gap-3 bg-pool hover:bg-pool-dark disabled:opacity-60 text-white font-bold text-xs sm:text-sm tracking-[0.15em] uppercase px-8 py-4 rounded-full transition-colors"
                  data-testid="casting-submit"
                >
                  {busy ? "Submitting…" : <>Submit to Poolside <ArrowRight className="h-4 w-4" /></>}
                </button>
                <p className="mt-4 text-[11px] text-slate-400 text-center leading-relaxed" data-testid="casting-disclaimer">
                  Submitting an application does not guarantee an appearance. The Poolside Cooking production team
                  reviews every submission and will contact selected applicants directly.
                </p>
              </form>
            </Reveal>
          )}
        </div>
      </section>

      {/* FINAL BRAND MOMENT */}
      <section className="water-panel text-white noise-overlay py-16 sm:py-20" data-testid="be-on-show-final">
        <img src="/assets/water.jpg" alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover opacity-20 pointer-events-none" />
        <div className="relative container-site text-center max-w-3xl mx-auto">
          <Reveal>
            <h2 className="font-display font-black tracking-tight leading-[1.02] text-4xl sm:text-5xl lg:text-6xl" data-testid="be-on-show-final-headline">
              Good Food. Good Vibes.<br />
              <span className="text-pool-light">Great Stories.</span>
            </h2>
            <p className="mt-5 font-script text-3xl sm:text-4xl text-pool-light" data-testid="be-on-show-signature">
              &ldquo;Chef, Where You At?&rdquo;
            </p>
            <Link
              to="/watch"
              data-testid="be-on-show-watch-cta"
              className="mt-9 inline-flex items-center gap-3 bg-white text-ink hover:bg-pool-light font-bold text-xs sm:text-sm tracking-[0.15em] uppercase px-8 py-4 rounded-full transition-all hover:-translate-y-0.5"
            >
              Watch Poolside Cooking <ArrowRight className="h-4 w-4" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
